import { FC, useState, useEffect, useRef } from "react";
import { Box, styled } from "@mui/material";
import { decode } from "tiff";
import { background } from "@/theme/colors";
import { useZoomPan } from "@/features/talent/components/talent-scan-image/use-zoom-pan";
import { ZoomControls } from "@/features/talent/components/talent-scan-image/ZoomControls";

type TImagePreviewWithZoomProps = {
  src: string | File;
  type: "image" | "tiff" | "pdf";
  width?: number | string;
  height?: number;
  /** Upscale factor for high-res rendering (default: 2). Higher = sharper zoom but more memory. */
  upscaleFactor?: number;
};

// Safety limits to prevent browser crashes from excessive memory usage
const MAX_CANVAS_DIMENSION = 16384; // Most browsers support up to 16384px
const MAX_CANVAS_PIXELS = 268435456; // ~268 million pixels (16384 x 16384)

/**
 * Calculates a safe upscale factor that won't exceed browser canvas limits.
 * Returns the actual factor that can be used safely.
 */
const calculateSafeUpscaleFactor = (imageWidth: number, imageHeight: number, requestedFactor: number): number => {
  // Start with requested factor and reduce if needed
  let factor = requestedFactor;

  while (factor > 1) {
    const upscaledWidth = imageWidth * factor;
    const upscaledHeight = imageHeight * factor;
    const totalPixels = upscaledWidth * upscaledHeight;

    // Check both dimension limits and total pixel count
    if (upscaledWidth <= MAX_CANVAS_DIMENSION && upscaledHeight <= MAX_CANVAS_DIMENSION && totalPixels <= MAX_CANVAS_PIXELS) {
      return factor;
    }

    // Reduce factor and try again
    factor -= 0.5;
  }

  return 1; // Fallback to no upscaling
};

/**
 * Upscales ImageData (from TIFF) using canvas for high-quality rendering.
 */
const upscaleImageDataWithCanvas = (
  imageData: ImageData,
  originalWidth: number,
  originalHeight: number,
  factor: number,
): { dataUrl: string; width: number; height: number } => {
  const safeFactor = calculateSafeUpscaleFactor(originalWidth, originalHeight, factor);
  const upscaledWidth = Math.floor(originalWidth * safeFactor);
  const upscaledHeight = Math.floor(originalHeight * safeFactor);

  // First canvas: render original ImageData at native size
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = originalWidth;
  sourceCanvas.height = originalHeight;
  const sourceCtx = sourceCanvas.getContext("2d");
  if (!sourceCtx) {
    return { dataUrl: "", width: originalWidth, height: originalHeight };
  }
  sourceCtx.putImageData(imageData, 0, 0);

  if (safeFactor <= 1) {
    // No upscaling, return native resolution
    return {
      dataUrl: sourceCanvas.toDataURL("image/png"),
      width: originalWidth,
      height: originalHeight,
    };
  }

  // Second canvas: upscale with high-quality interpolation
  const upscaleCanvas = document.createElement("canvas");
  upscaleCanvas.width = upscaledWidth;
  upscaleCanvas.height = upscaledHeight;
  const upscaleCtx = upscaleCanvas.getContext("2d");
  if (!upscaleCtx) {
    return {
      dataUrl: sourceCanvas.toDataURL("image/png"),
      width: originalWidth,
      height: originalHeight,
    };
  }

  upscaleCtx.imageSmoothingEnabled = true;
  upscaleCtx.imageSmoothingQuality = "high";
  upscaleCtx.drawImage(sourceCanvas, 0, 0, upscaledWidth, upscaledHeight);

  return {
    dataUrl: upscaleCanvas.toDataURL("image/png"),
    width: upscaledWidth,
    height: upscaledHeight,
  };
};

export const ImagePreviewWithZoom: FC<TImagePreviewWithZoomProps> = ({ src, type, width = "100%", height = 300, upscaleFactor = 2 }) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const { scale, position, isDragging, zoomIn, zoomOut, resetTo100, handlers } = useZoomPan({
    contentWidth: imageDimensions.width,
    contentHeight: imageDimensions.height,
    containerRef,
    maxFitScale: 1,
  });

  // Load image, upscale for high-quality zoom, and get dimensions
  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | null = null;

    const loadImage = async () => {
      // Handle TIFF files (already decoded to ImageData)
      if (type === "tiff" && src instanceof File) {
        try {
          const arrayBuffer = await src.arrayBuffer();
          const tiffData = decode(new Uint8Array(arrayBuffer));
          if (isCancelled) return;

          const tiffImageFrame = tiffData[0];
          if (!tiffImageFrame) return;

          const { width: imageWidth, height: imageHeight, data: tiffPixelData } = tiffImageFrame;

          // Create ImageData from TIFF pixel data
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = imageWidth;
          tempCanvas.height = imageHeight;
          const tempCtx = tempCanvas.getContext("2d");
          if (!tempCtx) return;

          const bytesPerPixel = tiffPixelData.length / (imageWidth * imageHeight);
          const canvasImageData = tempCtx.createImageData(imageWidth, imageHeight);
          const outputPixels = canvasImageData.data;

          if (bytesPerPixel === 4) {
            outputPixels.set(tiffPixelData);
          } else if (bytesPerPixel === 3) {
            for (let sourceIndex = 0, destIndex = 0; sourceIndex < tiffPixelData.length; sourceIndex += 3, destIndex += 4) {
              outputPixels[destIndex] = tiffPixelData[sourceIndex];
              outputPixels[destIndex + 1] = tiffPixelData[sourceIndex + 1];
              outputPixels[destIndex + 2] = tiffPixelData[sourceIndex + 2];
              outputPixels[destIndex + 3] = 255;
            }
          } else if (bytesPerPixel === 1) {
            for (let sourceIndex = 0, destIndex = 0; sourceIndex < tiffPixelData.length; sourceIndex++, destIndex += 4) {
              const grayValue = tiffPixelData[sourceIndex];
              outputPixels[destIndex] = grayValue;
              outputPixels[destIndex + 1] = grayValue;
              outputPixels[destIndex + 2] = grayValue;
              outputPixels[destIndex + 3] = 255;
            }
          }

          // Upscale the TIFF ImageData for high-quality zoom
          const upscaled = upscaleImageDataWithCanvas(canvasImageData, imageWidth, imageHeight, upscaleFactor);

          if (!isCancelled && upscaled.dataUrl) {
            setImageSrc(upscaled.dataUrl);
            setImageDimensions({ width: upscaled.width, height: upscaled.height });
          }
        } catch (decodeError) {
          console.error("TIFF decode failed:", decodeError);
        }
        return;
      }

      // Handle regular images (PNG, JPG, etc.). The browser decodes and scales
      // these natively, so the source is shown as-is: canvas pre-upscaling only
      // interpolates pixels the original never had, at a large memory cost.
      let imgSrc: string;
      if (typeof src === "string") {
        imgSrc = src;
      } else if (src instanceof File) {
        objectUrl = URL.createObjectURL(src);
        imgSrc = objectUrl;
      } else {
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (isCancelled) return;
        setImageSrc(imgSrc);
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        console.error("Image load failed");
      };
      img.src = imgSrc;
    };

    loadImage();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, type, upscaleFactor]);

  if (type === "pdf") {
    return null;
  }

  if (!imageSrc) {
    return <PreviewContainer width={width} height={height} />;
  }

  return (
    <PreviewContainer width={width} height={height}>
      <ImageViewport ref={containerRef} height={height - 44} isDragging={isDragging} {...handlers}>
        <ImageContainer
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "0 0",
          }}
        >
          <StyledImage ref={imageRef} src={imageSrc} alt="Preview" draggable={false} />
        </ImageContainer>
      </ImageViewport>
      <ZoomControls scale={scale} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={resetTo100} />
    </PreviewContainer>
  );
};

const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "height",
})<{ width: number | string; height: number }>`
  width: ${({ width }) => (typeof width === "number" ? `${width}px` : width)};
  height: ${({ height }) => height}px;
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${background.disable};
`;

const ImageViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "isDragging",
})<{ height: number; isDragging: boolean }>`
  flex: 1;
  overflow: hidden;
  height: ${({ height }) => height}px;
  cursor: ${({ isDragging }) => (isDragging ? "grabbing" : "grab")};
  position: relative;
`;

const ImageContainer = styled(Box)`
  position: absolute;
  will-change: transform;
`;

const StyledImage = styled("img")`
  display: block;
  user-select: none;
  /* Render upscaled image with smooth interpolation */
  image-rendering: auto;
`;
